import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const commonBase = [
  '教学目标与结构',
  '理论与内容准确性',
  '案例和资源使用',
  '问题设计与互动',
  '节奏管理',
  '价值引导与教学效果',
];

const courseCards = [
  {
    name: '习思想概论',
    highlight: '理论、政策、现实案例和价值目标之间能否形成完整联系',
    color: 'primary' as const,
  },
  {
    name: '原理',
    highlight: '概念是否准确、论证是否展开、原理能否用于解释现实',
    color: 'accent' as const,
  },
  {
    name: '纲要',
    highlight: '史实准确、历史线索、因果关系和历史评价',
    color: 'primary' as const,
  },
  {
    name: '德法',
    highlight: '学生现实问题、规范判断和行为引导',
    color: 'accent' as const,
  },
  {
    name: '毛中特概论',
    highlight: '理论体系、历史逻辑和现实发展之间的联系',
    color: 'primary' as const,
  },
];

export default function P68WrapUp({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      <div ref={orb1Ref} className="absolute top-20 right-24 w-80 h-80 rounded-full bg-primary-100/10 blur-[100px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-28 left-12 w-96 h-96 rounded-full bg-accent-100/8 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-6 md:py-8">
        <div ref={titleRef} className="text-center mb-3 md:mb-4">
          <span className={`inline-flex items-center gap-2 text-xs font-body font-semibold text-gold-600 uppercase tracking-[0.15em] mb-4 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <span className="gold-dot" />共同底座 + 课程模型
          </span>
          <h2 className={`font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            同一套技术底座，不同的<span className="text-gold-600">课程判断重点</span>
          </h2>
          <p className={`text-sm text-foreground-600 font-body max-w-2xl mx-auto leading-relaxed transition-all duration-1000 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            一套平台可以承载多门课程，但不能用一把尺子替代所有课程的专业判断
          </p>
        </div>

        {/* Upper layer: common base */}
        <div className={`max-w-5xl mx-auto mb-3 transition-all duration-1000 delay-300 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <div className="p-4 md:p-5 glass-liquid rounded-xl">
            <div className="text-center mb-3">
              <span className="inline-block px-3 py-1 rounded-full text-xs font-body font-bold bg-primary-100/60 text-primary-700 border border-primary-200/40">
                共性评价底座
              </span>
            </div>
            <div className="flex flex-wrap justify-center gap-2">
              {commonBase.map((item, i) => (
                <span
                  key={item}
                  className="px-3 py-1.5 rounded-full text-xs font-body font-semibold bg-secondary-100/60 text-secondary-700 border border-secondary-200/30"
                  style={{ transitionDelay: `${300 + i * 80}ms` }}
                >
                  {item}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Lower layer: course cards */}
        <div className={`max-w-5xl mx-auto mb-3 transition-all duration-1000 delay-400 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 md:gap-3">
            {courseCards.map((c, i) => (
              <div
                key={c.name}
                className={`p-3 rounded-xl border text-center ${
                  c.color === 'accent'
                    ? 'bg-gold-100/60 border-gold-200/50'
                    : 'bg-primary-100/30 border-primary-200/40'
                }`}
                style={{ transitionDelay: `${400 + i * 80}ms` }}
              >
                <h4 className={`text-xs font-body font-bold mb-1.5 ${c.color === 'accent' ? 'text-gold-700' : 'text-primary-700'}`}>
                  {c.name}
                </h4>
                <p className="text-[10px] text-foreground-500 font-body leading-relaxed">{c.highlight}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Center conclusion */}
        <div className={`max-w-2xl mx-auto mb-3 transition-all duration-1000 delay-600 ${titleVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
          <div className="p-4 rounded-xl glass-gold border border-gold-200/40 text-center">
            <p className="font-heading text-base md:text-lg font-bold text-gold-800">
              技术能力可以共享，评价标准必须分课程建设
            </p>
          </div>
        </div>

        <div className={`max-w-3xl mx-auto text-center transition-all duration-1000 delay-700 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="p-4 glass-liquid rounded-xl">
            <p className="text-sm text-foreground-600 font-body leading-relaxed">
              真正的跨课程AI评课，需要为每门课程分别建设<strong className="text-gold-700">教材知识库、课程知识图谱、专属评价点、专家样本和校准数据</strong>。
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}